import { ChangeDetectorRef, Component } from '@angular/core';
import { MediaObserver } from '@angular/flex-layout';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import moment from 'moment';
import { take } from 'rxjs';
import { ITableResponsive } from 'src/app/domain/ITableResponsive';
import { MessagesEnum } from 'src/app/domain/enums/messages.enum';
import { getSystemTypeDescriptionByValue } from 'src/app/domain/registration/enums/system-type.enum';
import { TypeCesEnum } from 'src/app/domain/registration/enums/type-ces.enum';
import { TypeIndexEnum } from 'src/app/domain/registration/enums/type-index.enum';
import { IPlan } from 'src/app/domain/registration/models/IPlan';
import { AbstractCrudIndex } from 'src/app/shared/components/crud/abstract-crud-index';
import { DialogConfirmComponent } from 'src/app/shared/components/dialog-confirm/dialog-confirm.component';
import { GlobalUtils } from 'src/app/shared/global-utils';
import { PermissionService } from 'src/app/shared/permission.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { AuthService } from 'src/app/views/auth/shared/auth.service';
import { PlanPermissionsComponent } from '../../plan-permissions/plan-permissions.component';
import { ValueReferenceCrudIndex } from '../../value-reference/crud-index/crud-index';
import { PlanCESComponent } from '../crud-ces/crud-ces.component';
import {
  COLUMNS_TO_SHOW_MOBILE,
  COLUMNS_TO_SHOW_WEB,
  TABLE_CONFIG_COLUMNS,
} from '../crud-config';
import { PlanService } from '../crud-service';
import { PlanCreationDialogComponent } from './plan-creation-dialog/plan-creation-dialog.component';
import { PlanDetailsComponent } from './plan-details/plan-details.component';
import { TypeComponentFieldEnum } from 'src/app/domain/registration/enums/type-component-dynamic.enum';
import { Plan } from './plans-interfaces';

@Component({
  selector: 'app-plan-index',
  templateUrl: './crud-index.component.html',
  styleUrls: ['./crud-index.component.sass'],
  providers: [PlanService],
})
export class PlanCrudIndex
  extends AbstractCrudIndex<IPlan>
  implements ITableResponsive {
  override title = 'PLANOS';
  override displayedColumns = COLUMNS_TO_SHOW_WEB;
  override configColumns = TABLE_CONFIG_COLUMNS;

  currentTab: string = 'plans';
  userType: string = '';

  viewMode: 'table' | 'cards' = 'cards';
  filterFormValues: any = null;
  constructor(
    public override dialog: MatDialog,
    public override service: PlanService,
    public override toastService: ToastService,
    public override mediaObserver: MediaObserver,
    public override router: Router,
    protected cdr: ChangeDetectorRef,
    protected authService: AuthService,
    public permissionService: PermissionService,

  ) {
    super(dialog, service, toastService, mediaObserver, router);

    this.userType = this.authService.isPartner() ? 'partner' : 'admin';
    this.currentTab = 'systems';

    mediaObserver.asObservable().subscribe((res) => {
      this.is_mobile = res[0].mqAlias === 'sm' || res[0].mqAlias === 'xs';
      this.displayedColumns = this.is_mobile
        ? COLUMNS_TO_SHOW_MOBILE
        : COLUMNS_TO_SHOW_WEB;
    });
  }

  ngAfterViewInit(): void {
    this.loadList();
  }

  override loadList() {
    let params = this.params;

    this.service.listAll(params).subscribe((dataWithPagination: any) => {
      const [data, length] = dataWithPagination;
      this.length = length;

      const allItems = data.map((item: any) => {
        const isGeneralPlan = !item.partners || item.partners.length === 0;
        return {
          ...item,
          image: item.system?.image,
          partners: this.getPartnersLabel(item.partners),
          type: getSystemTypeDescriptionByValue(item.type),
          system: item.system?.name,
          createdAt: item.createdAt,
          dueDate: (item?.dueDate !== null && item?.dueDate !== undefined && String(item?.dueDate).trim() !== '') ? item.dueDate : item.system?.dueDate,
          hideEdit: !item.allowEdit,
          hideDelete: !item.allowDelete,
          hasAudit: item.audits?.length > 0,
          isGeneralPlan: isGeneralPlan,
          rowClass: isGeneralPlan ? 'general-plan' : '',
          isActive: item.isActive,
          partnersList: item.partners,
          hasPartners: item.partners.length > 0 ? 'SIM' : 'NÃO'
        };
      });

      const sortedItems = allItems.sort((a: any, b: any) => {
        if (a.isGeneralPlan !== b.isGeneralPlan) {
          return a.isGeneralPlan ? -1 : 1;
        }

        const dateA = moment(a.createdAt, 'DD/MM/YYYY HH:mm');
        const dateB = moment(b.createdAt, 'DD/MM/YYYY HH:mm');
        if (!dateA.isSame(dateB)) {
          return dateB.valueOf() - dateA.valueOf();
        }

        const aIsActive = a.isActive !== false;
        const bIsActive = b.isActive !== false;
        if (aIsActive === bIsActive) return 0;
        return aIsActive ? -1 : 1;
      });

      this.dataSource = new MatTableDataSource<any>(sortedItems);

      if (this.dataSource.paginator) {
        this.dataSource.paginator.length = length;
      }
    });
  }

  public getPartnersLabel(partners: any) {
    return partners?.map((p: any) => p.companyName || p.name).join(', ');
  }

  public openShow(element: Plan) {
    GlobalUtils.openDialog({
      dialog: this.dialog,
      dialogConfig: { width: '45%' },
      component: PlanCESComponent,
      data: { type: TypeCesEnum.SHOW, element },
      callBack: (data: any) => {
        if (data?.changeType === TypeCesEnum.EDIT) {
          this.openEdit(element);
        }

        if (data?.result) {
          this.openDelete(element);
        }
      },
    });
  }

  public openSettings(element: any): void {
    const dialogRef = this.dialog.open(ValueReferenceCrudIndex, {
      width: '40%',
      data: element
    });

    dialogRef.close();
  }

  public openNew(element: any) {
    this.openNewCreation(element);
  }

  public openNewCreation(element?: any) {
    this.openPlanCreationDialog(TypeCesEnum.NEW, element);
  }

  private openPlanCreationDialog(type: TypeCesEnum, element?: any): void {
    GlobalUtils.openDialog({
      dialog: this.dialog,
      dialogConfig: {
        width: '90vw',
        maxWidth: '90vw',
        height: '90vh',
        minHeight: '90vh',
        maxHeight: '90vh',
      },
      component: PlanCreationDialogComponent,
      data: { type, element },
      callBack: (data: any) => {
        if (!data?.element || type === TypeCesEnum.SHOW) {
          return;
        }

        const isEditFlow = type === TypeCesEnum.EDIT && !!element?.id;
        if (isEditFlow) {
          const updateId = element.id;
          const retryElement = Object.assign({}, element, data.element, { id: updateId });
          this.service.update(updateId, Object.assign({}, data.element, { id: updateId })).subscribe({
            next: () => {
              this.loadList();
              this.toastService.showSuccess(MessagesEnum.SUCCESS_UPDATED);
            },
            error: (error) => {
              this.openPlanCreationDialog(TypeCesEnum.EDIT, retryElement);
            },
          });
          return;
        }

        const createPayload = Object.assign({}, data.element);
        delete createPayload.id;

        this.service.create(createPayload).subscribe(
          () => {
            this.loadList();
            this.toastService.showSuccess(MessagesEnum.SUCCESS_CREATED);
          },
          (error) => {
            this.openNewCreation(data.element);
          }
        );
      },
    });
  }

  public openEdit(element: Plan) {
    GlobalUtils.openDialog({
      dialog: this.dialog,
      dialogConfig: { width: '35%' },
      component: DialogConfirmComponent,
      data: {
        title: 'Você tem certeza?',
        content: 'Ao alterar o plano estará executando ações irreversíveis no sistema.',
        noText: 'CANCELAR',
        yesText: 'CONTINUAR',
      },
      callBack: (data: any) => {
        if (data?.result) {
          this.openEditEfective(element);
        }
      },
    });
  }

  public openEditEfective(element: Plan) {
    this.openPlanCreationDialog(TypeCesEnum.EDIT, element);
  }

  public openPermissions($event: any) {
    GlobalUtils.openDialog({
      dialog: this.dialog,
      dialogConfig: {
        width: '90%',
        height: 'auto',
        enterAnimationDuration: '500ms',
        exitAnimationDuration: '500ms',
        disableClose: false,
        panelClass: 'modal-panel',
      },
      component: PlanPermissionsComponent,
      data: { type: TypeIndexEnum.CRUD, select: true, element: $event },
      callBack: (data: any) => {
      },
    });
  }

  public lock(plan: any) {
    GlobalUtils.openDialog({
      dialog: this.dialog,
      data: { title: `Bloquear plano => sistema: ${plan.system}, terminais: ${plan.terminalQuantity}` },
      component: DialogConfirmComponent,
      callBack: (data: any) => {
        if (data?.result) {
          this.service
            .changeActiveStatus(plan.id!, false)
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.toastService.showSuccess(MessagesEnum.PLAN_LOCK_SUCCESS);
                this.loadList();
              },
              error: (e) => console.log(e),
            });
        }
      },
    });
  }

  public unlock(plan: any) {
    GlobalUtils.openDialog({
      dialog: this.dialog,
      data: { title: `Desbloquear plano => sistema: ${plan.name}, terminais: ${plan.terminalQuantity}` },
      component: DialogConfirmComponent,
      callBack: (data: any) => {
        if (data?.result) {
          this.service
            .changeActiveStatus(plan.id!, true)
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.toastService.showSuccess(
                  MessagesEnum.PLAN_UNLOCK_SUCCESS
                );
                this.loadList();
              },
              error: (e) => console.log(e),
            });
        }
      },
    });
  }

  public info(element: any) {
    GlobalUtils.openDialog({
      dialog: this.dialog,
      dialogConfig: {
        width: '1000px',
        height: 'auto',
        maxHeight: '95vh',
      },
      component: PlanDetailsComponent,
      data: { element },
      callBack: (data: any) => {
      },
    });
  }

  toggleViewMode(): void {
    this.viewMode = this.viewMode === 'table' ? 'cards' : 'table';
  }

  public resetFilter(): void {
    this.filterFormValues = null;
    localStorage.removeItem('parcllilterFormValues');

    this.isAdvancedFilterActive = false;
    this.lastAppliedFilter = null;
    this.params = { limit: 25, page: 0, filter: '' };

    const resetFields = this.filterFields.map(field => {
      if (field.type === TypeComponentFieldEnum.SLIDE_TOGGLE) {
        if (field.name === 'isActive') {
          return { ...field, value: true };
        }
        return { ...field, value: false };
      }
      return { ...field, value: '' };
    });

    this.filterFields = resetFields;

    this.loadList();
  }
}
